import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { WebhookEndpoint } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useWebhookEvents } from '../hooks/use-webhook-events';
import { useCreateWebhook, useUpdateWebhook } from '../hooks/use-webhook-mutations';
import {
  emptyEndpointForm,
  endpointFormSchema,
  fromEndpoint,
  toEndpointCreate,
  toEndpointUpdate,
  type EndpointForm,
} from '../schemas/endpoint-form-schema';

import { EventsPicker } from './events-picker';
import { SecretPanel } from './secret-panel';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['url', 'description', 'events']);
// `events[3]` from the backend lands on the picker as a whole.
const mapField = (field: string) => (field.startsWith('events') ? 'events' : field);

export interface EndpointDialogProps {
  storeId: string;
  /** Null creates a new endpoint. */
  endpoint: WebhookEndpoint | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function EndpointFormBody({
  storeId,
  endpoint,
  known,
  onSecret,
  onClose,
}: {
  storeId: string;
  endpoint: WebhookEndpoint | null;
  known: readonly string[];
  onSecret: (secret: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(['webhooks', 'common']);
  const message = useErrorMessage();
  const create = useCreateWebhook(storeId);
  const update = useUpdateWebhook(storeId);
  const pending = create.isPending || update.isPending;
  const schema = useMemo(() => endpointFormSchema(known), [known]);
  const form = useForm<EndpointForm>({
    resolver: useI18nResolver(schema),
    defaultValues: endpoint ? fromEndpoint(endpoint) : emptyEndpointForm(),
  });

  const submit = form.handleSubmit(async (values) => {
    try {
      if (endpoint) {
        await update.mutateAsync({ id: endpoint.id, body: toEndpointUpdate(values) });
        onClose();
      } else {
        const created = await create.mutateAsync(toEndpointCreate(values));
        onSecret(created.secret);
      }
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {form.formState.errors.root?.server?.message ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="url"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('webhooks:fields.url')}</FormLabel>
              <FormControl>
                <Input
                  type="url"
                  autoComplete="off"
                  className="font-mono text-[13px]"
                  placeholder="https://erp.example.com/nucleus"
                  autoFocus
                  {...field}
                />
              </FormControl>
              <FormDescription>{t('webhooks:fields.urlHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('webhooks:fields.description')}</FormLabel>
              <FormControl>
                <Input autoComplete="off" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="events"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('webhooks:fields.events')}</FormLabel>
              <EventsPicker
                known={known}
                value={field.value}
                onChange={field.onChange}
                invalid={fieldState.invalid}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button type="submit" disabled={pending}>
            {pending
              ? t('common:actions.saving')
              : endpoint
                ? t('webhooks:edit.submit')
                : t('webhooks:create.submit')}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/** Registers or edits an endpoint. A new endpoint's signing secret is shown once, here. */
export function EndpointDialog({ storeId, endpoint, open, onOpenChange }: EndpointDialogProps) {
  const { t } = useTranslation('webhooks');
  const events = useWebhookEvents(storeId);
  // Held in component state only; closing the dialog drops the secret for good.
  const [secret, setSecret] = useState<string | null>(null);

  const close = () => {
    onOpenChange(false);
    setSecret(null);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent
        className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          if (secret) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {secret ? t('secret.createdTitle') : endpoint ? t('edit.title') : t('create.title')}
          </DialogTitle>
          <DialogDescription>
            {secret
              ? t('secret.createdDescription')
              : endpoint
                ? t('edit.description')
                : t('create.description')}
          </DialogDescription>
        </DialogHeader>
        {secret ? (
          <SecretPanel secret={secret} onDone={close} />
        ) : events.data ? (
          <EndpointFormBody
            storeId={storeId}
            endpoint={endpoint}
            known={events.data}
            onSecret={setSecret}
            onClose={close}
          />
        ) : (
          <Skeleton className="h-64" />
        )}
      </DialogContent>
    </Dialog>
  );
}
